import Link from 'next/link';
import { Linkedin, Instagram, Facebook } from 'lucide-react';
import Logo from '@/components/Logo';
import { CookiePreferencesLink } from './CookiePreferencesLink';
import { SHOW_PRICING } from '@/lib/feature-flags';
import { siteConfig } from '@/lib/config';

/* ── Inline SVG icons for platforms not yet in lucide-react ── */
const PinterestIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z" />
  </svg>
);


const socialLinks = [
  { href: 'https://instagram.com/laurelwebco',  label: 'Instagram (opens in new tab)',  Icon: Instagram   },
  { href: 'https://linkedin.com/company/laurelwebco',   label: 'LinkedIn (opens in new tab)',   Icon: Linkedin    },
  { href: 'https://facebook.com/laurelwebco',   label: 'Facebook (opens in new tab)',   Icon: Facebook    },
  { href: 'https://pinterest.com/laurelwebco',  label: 'Pinterest (opens in new tab)',  Icon: PinterestIcon },
];

const CURRENT_YEAR = new Date().getFullYear();

const Footer = () => (
  <footer className="bg-bloom-green-deep text-bloom-cream/90 print:hidden" aria-label="Site footer">
    <div className="container mx-auto px-6 py-20 lg:py-24">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-16">
        {/* Col 1 — Brand */}
        <div className="lg:col-span-2">
          <Logo variant="light" className="h-20 w-auto" />
          <p className="mt-8 body-large !text-bloom-cream/70 max-w-sm">
            Strategy, design, and accessibility-first web development for the public sector.
          </p>
          <p className="mt-4 body-large !text-bloom-gold font-serif italic">
            Websites built to serve the people who serve others.
          </p>

          {/* Social Icons */}
          <div className="flex flex-wrap gap-4 mt-8">
            {socialLinks.map(({ href, label, Icon }) => (
              <a
                key={label}
                href={href || '#'}
                aria-label={label}
                className="text-bloom-cream/50 hover:text-bloom-gold transition-all duration-300 focus-bloom-light rounded-md p-1.5 bg-white/5 hover:bg-white/10"
                rel="noopener noreferrer"
                target="_blank"
              >
                <Icon size={18} aria-hidden="true" />
              </a>
            ))}
          </div>
        </div>

        {/* Col 2 — Solutions */}
        <div>
          <h3 className="font-serif text-lg font-semibold text-bloom-cream mb-6">Solutions</h3>
          <ul className="space-y-4 font-sans text-[15px]" role="list">
            {[
              { label: 'Small Business', href: '/small-business-web-design' },
              { label: 'Nonprofits', href: '/nonprofit-web-design-georgia' },
              { label: 'Churches', href: '/church-website-design' },
              { label: 'HOAs', href: '/hoa-website-design' },
              { label: 'Grant-Ready Sites', href: '/grant-ready-website' },
              // { label: 'ADA Title II', href: '/ada-title-ii-compliance' },
              { label: 'Accessibility Audit', href: '/website-accessibility-audit' },
            ].map((item) => (
              <li key={item.href}>
                <Link href={item.href} className="text-bloom-cream/60 hover:text-bloom-gold transition-colors focus-bloom-light rounded-md py-1 inline-block">
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Col 3 — Company */}
        <div>
          <h3 className="font-serif text-lg font-semibold text-bloom-cream mb-6">Company</h3>
          <ul className="space-y-4 font-sans text-[15px]" role="list">
            {['Services', 'About', 'Blog', 'Contact'].map((item) => {
              const hrefMap: Record<string, string> = {
                'Services': '/services',
                'About': '/about',
                'Blog': '/blog',
                'Contact': '/contact',
              };
              return (
                <li key={item}>
                  <Link href={hrefMap[item]} className="text-bloom-cream/60 hover:text-bloom-gold transition-colors focus-bloom-light rounded-md py-1 inline-block">
                    {item}
                  </Link>
                </li>
              );
            })}
            {SHOW_PRICING && (
              <li>
                <Link href="/estimate" className="text-bloom-gold hover:text-bloom-gold/80 transition-colors focus-bloom-light rounded-md py-1 inline-block font-bold">
                  Cost Estimator
                </Link>
              </li>
            )}
          </ul>
        </div>
      </div>
    </div>

    {/* Bottom bar */}
    <div className="border-t border-white/5 bg-black/5">
      <div className="container mx-auto px-6 py-10 flex flex-col md:flex-row items-center justify-between gap-8">
        <div className="flex flex-col gap-2 items-center md:items-start text-center md:text-left">
          <p className="font-sans text-[13px] text-bloom-cream/40">
            © {CURRENT_YEAR} Becoming and Bloom LLC. All rights reserved.
          </p>
          <p className="font-sans text-[11px] text-bloom-cream/25 max-w-sm">
            {siteConfig.name} is {siteConfig.division}. Web transformations with a craft-first mindset in North Georgia.
          </p>
        </div>
        
        <nav aria-label="Legal links" className="flex flex-wrap justify-center gap-x-8 gap-y-4">
          {[
            { label: 'Privacy', href: '/privacy-policy' },
            { label: 'Accessibility', href: '/accessibility' },
            { label: 'Terms', href: '/terms' },
          ].map(link => (
            <Link key={link.href} href={link.href} className="label-text !text-[11px] !text-bloom-cream/40 hover:!text-bloom-gold transition-colors focus-bloom-light rounded-md">
              {link.label}
            </Link>
          ))}
          <div className="label-text !text-[11px] !text-bloom-cream/40 px-2 lg:px-0">
            <CookiePreferencesLink />
          </div>
        </nav>
      </div>
    </div>
  </footer>
);

export default Footer;
